import { useState, useEffect, useCallback } from 'react';
import {
  Building2, Plus, Shield, Loader2, Search, CheckCircle, XCircle,
  X, Mail, Lock, Eye, EyeOff, LogOut, BarChart3, Settings,
  Database, Users, AlertTriangle, Edit, Trash2, RefreshCw,
  ChevronRight, Activity, Globe, Crown, Bell, Send,
  Download, FileText, DollarSign, ClipboardList, ExternalLink,
  ToggleLeft, ToggleRight, KeyRound, Info, Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const SA_TOKEN_KEY = 'superadmin_token';

// ── Dedicated axios — bypasses all shared interceptors ────────────────────────
const saAxios = axios.create({ baseURL: '/api/superadmin', headers: { 'Content-Type': 'application/json' } });
saAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem(SA_TOKEN_KEY);
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

// ── Helpers ───────────────────────────────────────────────────────────────────
const PLAN_COLORS = {
  enterprise: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  standard:   'bg-sky-500/20 text-sky-400 border-sky-500/30',
  free:       'bg-slate-700/50 text-slate-400 border-slate-600/30',
};
const PLAN_PRICE = { free: 0, standard: 49, enterprise: 199 };

function PlanBadge({ plan }) {
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${PLAN_COLORS[plan] || PLAN_COLORS.standard}`}>
      {plan?.charAt(0).toUpperCase() + plan?.slice(1)}
    </span>
  );
}

function StatCard({ label, value, color = 'text-white', icon: Icon, sub }) {
  return (
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-slate-600" />}
      </div>
      <div className={`text-3xl font-black ${color}`}>{value}</div>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

// ── Login screen ──────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('superadmin@trivont.com');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setTimeout(() => setMounted(true), 100); }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Email and password required'); return; }
    setLoading(true);
    try {
      const res = await saAxios.post('/auth/login/', { email, password });
      localStorage.setItem(SA_TOKEN_KEY, res.data.token);
      toast.success(`Welcome, ${res.data.name}`);
      onLogin(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid credentials');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-slate-950 overflow-hidden">

      {/* ── LEFT PANEL — branding ── */}
      <div className="hidden lg:flex lg:flex-1 relative flex-col justify-between p-12 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0f172a 0%, #0c1a2e 50%, #0a1628 100%)' }}>
        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-5"
          style={{ backgroundImage: 'linear-gradient(#38bdf8 1px,transparent 1px),linear-gradient(90deg,#38bdf8 1px,transparent 1px)', backgroundSize: '40px 40px' }} />
        {/* Glow orbs */}
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-500/8 rounded-full blur-3xl" />

        <div className="relative z-10">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-16"
            style={{ opacity: mounted ? 1 : 0, transition: 'opacity 0.8s' }}>
            <div className="w-10 h-10 bg-gradient-to-br from-sky-400 to-sky-600 rounded-xl flex items-center justify-center shadow-lg shadow-sky-500/30">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-black text-white text-sm tracking-tight">TRIVONT Technologies</span>
              <span className="block text-sky-400 text-xs font-medium">SupportOne Tenant Admin Portal</span>
            </div>
          </div>

          <div style={{ opacity: mounted ? 1 : 0, transition: 'opacity 0.8s 0.2s' }}>
            <h2 className="text-3xl xl:text-4xl font-bold text-white leading-snug mb-2">
              Multi-Tenant<br /><span className="text-sky-400">Control Center</span>
            </h2>
            <p className="text-white/50 text-sm">Provision tenants &middot; Manage billing &middot; Monitor usage &middot; Global announcements</p>
          </div>

          {/* Feature list */}
          <div className="mt-10 space-y-4"
            style={{ opacity: mounted ? 1 : 0, transition: 'opacity 0.8s 0.5s' }}>
            {[
              { icon: Building2, label: 'Tenant Provisioning', sub: 'Create isolated databases in seconds' },
              { icon: Database, label: 'Database Isolation', sub: 'Separate PostgreSQL per tenant' },
              { icon: Shield, label: 'Superadmin Security', sub: 'JWT-protected, session-scoped access' },
              { icon: BarChart3, label: 'Usage Analytics', sub: 'Monitor tickets, users, DB size per tenant' },
            ].map(({ icon: Icon, label, sub }) => (
              <div key={label} className="flex items-start gap-3">
                <div className="w-8 h-8 bg-sky-500/10 border border-sky-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon className="w-4 h-4 text-sky-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">{label}</p>
                  <p className="text-white/40 text-xs">{sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10" style={{ opacity: mounted ? 1 : 0, transition: 'opacity 0.8s 1s' }}>
          <span className="text-white/20 text-xs">TRIVONT Technologies SupportOne &copy; 2026</span>
        </div>
      </div>

      {/* ── RIGHT PANEL — Login Form ── */}
      <div className="flex-1 lg:flex-none lg:w-1/2 flex flex-col justify-center items-center px-8 sm:px-14 xl:px-20 py-12 bg-slate-950 lg:bg-slate-900">
        <div className="w-full max-w-md">
          {/* Mobile + desktop logo */}
          <div className="text-center mb-8 lg:mb-10">
            <div className="w-14 h-14 bg-gradient-to-br from-sky-400 to-sky-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-sky-500/30">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-0.5">Tenant Admin Portal</h1>
            <p className="text-sky-400 text-sm">TRIVONT Technologies SupportOne</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-xs text-slate-400 font-medium block mb-2">Email address</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 transition-all"
                  placeholder="superadmin@trivont.com" autoFocus />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 font-medium block mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl pl-10 pr-11 py-3 text-sm focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 transition-all"
                  placeholder="Enter superadmin password" />
                <button type="button" onClick={() => setShowPw(p => !p)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full bg-sky-500 hover:bg-sky-400 text-white font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-60 shadow-lg shadow-sky-500/20 mt-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
              Sign In to Admin Portal
            </button>
          </form>

          <p className="text-center text-slate-600 text-xs mt-6">
            Protected area &mdash; authorised personnel only
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Tenant row with expanded detail ───────────────────────────────────────────
function TenantRow({ t, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [statsError, setStatsError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ plan: t.plan, max_users: t.max_users, name: t.name });
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [showResetPw, setShowResetPw] = useState(false);
  const [newPw, setNewPw] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [lastBackup, setLastBackup] = useState(null);

  const loadStats = async () => {
    setLoadingStats(true);
    setStatsError('');
    try {
      const res = await saAxios.get(`/tenants/${t.id}/stats/`);
      setStats(res.data);
      if (res.data.errors?.length) {
        setStatsError(res.data.errors.join(' | '));
      }
    } catch (err) {
      setStatsError(err.response?.data?.error || 'Failed to load stats');
    } finally { setLoadingStats(false); }
  };

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    // Auto-load stats when expanding
    if (next && !stats && !loadingStats) loadStats();
  };

  const handleSave = async () => {
    setActionLoading('edit');
    try {
      await saAxios.patch(`/tenants/${t.id}/`, editForm);
      toast.success('Tenant updated');
      setEditMode(false);
      onRefresh();
    } catch { toast.error('Failed to update'); }
    finally { setActionLoading(''); }
  };

  const handleSuspend = async () => {
    setActionLoading('suspend');
    try {
      await saAxios.post(`/tenants/${t.id}/suspend/`);
      toast.success(`${t.name} suspended`);
      onRefresh();
    } catch { toast.error('Failed'); }
    finally { setActionLoading(''); }
  };

  const handleReactivate = async () => {
    setActionLoading('reactivate');
    try {
      await saAxios.post(`/tenants/${t.id}/reactivate/`);
      toast.success(`${t.name} reactivated`);
      onRefresh();
    } catch { toast.error('Failed'); }
    finally { setActionLoading(''); }
  };

  const handleDelete = async () => {
    if (deleteConfirm !== t.slug) { toast.error(`Type "${t.slug}" to confirm`); return; }
    setActionLoading('delete');
    try {
      await saAxios.delete(`/tenants/${t.id}/`, { data: { confirm_slug: t.slug } });
      toast.success(`${t.name} permanently deleted`);
      setShowDelete(false);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setActionLoading(''); }
  };

  const handleResetPassword = async () => {
    if (!newPw || newPw.length < 8) { toast.error('Minimum 8 characters'); return; }
    setActionLoading('reset');
    try {
      const res = await saAxios.post(`/tenants/${t.id}/reset_admin_password/`, { password: newPw });
      toast.success(`Password reset for ${res.data.email}`);
      setShowResetPw(false);
      setNewPw('');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setActionLoading(''); }
  };

  const handleImpersonate = async () => {
    setActionLoading('impersonate');
    try {
      const res = await saAxios.post(`/tenants/${t.id}/impersonate/`);
      toast.success(`Opening as ${res.data.email}`);
      window.open(res.data.login_url, '_blank');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setActionLoading(''); }
  };

  const handleReprovision = async () => {
    if (!window.confirm(`Re-run provisioning for "${t.name}"? This will recreate the database.`)) return;
    setActionLoading('reprovision');
    try {
      const res = await saAxios.post(`/tenants/${t.id}/reprovision/`);
      toast.success(res.data.detail || 'Reprovisioning started');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.error || 'Reprovisioning failed'); }
    finally { setActionLoading(''); }
  };

  const handleBackup = async () => {
    setActionLoading('backup');
    try {
      const res = await saAxios.post(`/tenants/${t.id}/backup/`);
      toast.success(`Backup complete — ${res.data.file_size_mb}MB`);
      setLastBackup(res.data);
    } catch (err) { toast.error(err.response?.data?.error || 'Backup failed'); }
    finally { setActionLoading(''); }
  };

  const handleSetupNginx = async () => {
    setActionLoading('nginx');
    try {
      const res = await saAxios.post(`/tenants/${t.id}/setup_nginx/`);
      toast.success(`SSL/Nginx: ${res.data.detail?.slice(0, 120) || 'Done'}`);
    } catch (err) { toast.error(err.response?.data?.error || 'SSL setup failed'); }
    finally { setActionLoading(''); }
  };

  const isLoading = (action) => actionLoading === action;

  return (
    <>
      {/* Main row */}
      <div className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-slate-800/40 hover:bg-slate-800/20 transition-colors items-center">
        <div className="col-span-3 flex items-center gap-3">
          <div className="w-9 h-9 bg-sky-500/10 border border-sky-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4 text-sky-400" />
          </div>
          <div>
            <p className="font-semibold text-white text-sm">{t.name}</p>
            <p className="text-xs text-slate-500 font-mono">{t.slug}</p>
            {t.domain && (
              <a href={`https://${t.domain}`} target="_blank" rel="noopener noreferrer"
                className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1 mt-0.5">
                <ExternalLink className="w-3 h-3" /> {t.domain}
              </a>
            )}
          </div>
        </div>
        <div className="col-span-2">
          <p className="font-mono text-xs text-emerald-400">{t.db_name}</p>
          <p className="text-xs text-slate-600">{t.db_host}:{t.db_port}</p>
        </div>
        <div className="col-span-1"><PlanBadge plan={t.plan} /></div>
        <div className="col-span-1">
          {t.is_active
            ? <span className="flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle className="w-3.5 h-3.5" /> Active</span>
            : <span className="flex items-center gap-1 text-rose-400 text-xs"><XCircle className="w-3.5 h-3.5" /> Suspended</span>}
        </div>
        <div className="col-span-1">
          <span className={`text-xs font-medium ${t.is_provisioned ? 'text-emerald-400' : 'text-amber-400 animate-pulse'}`}>
            {t.is_provisioned ? '✓ Ready' : '⏳ Pending'}
          </span>
        </div>
        <div className="col-span-1 text-xs text-slate-500">
          {t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}
        </div>
        <div className="col-span-3 flex items-center gap-1 justify-end">
          <button onClick={toggleExpand}
            className={`p-1.5 rounded-lg transition-all text-xs ${expanded ? 'bg-sky-500/20 text-sky-400' : 'text-slate-500 hover:text-white hover:bg-slate-700'}`}
            title="Expand details">
            <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </button>
        </div>
      </div>

      {/* Expanded detail panel */}
      {expanded && (
        <div className="border-b border-slate-800/40 bg-slate-900/40 px-5 py-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Stats */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Usage Statistics</h4>
                <button onClick={loadStats} disabled={loadingStats} className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1">
                  <RefreshCw className={`w-3 h-3 ${loadingStats ? 'animate-spin' : ''}`} /> Refresh
                </button>
              </div>
              {loadingStats ? (
                <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading stats...</div>
              ) : stats ? (
                <div className="space-y-3">
                  {/* DB Size — prominent display */}
                  <div className="bg-slate-800/80 rounded-xl p-3 flex items-center justify-between border border-slate-700/50">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-emerald-400" />
                      <span className="text-xs text-slate-400">Database Size</span>
                    </div>
                    <div className="text-right">
                      <span className="text-emerald-400 font-bold text-sm">{stats.db_size || 'N/A'}</span>
                      {stats.db_size_mb > 0 && <span className="text-xs text-slate-500 ml-1">({stats.db_size_mb} MB)</span>}
                    </div>
                  </div>
                  {/* Stats grid */}
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: 'Users', value: `${stats.active_users}/${stats.users}`, sub: 'active/total', color: 'text-sky-400' },
                      { label: 'Incidents', value: stats.incidents, sub: `${stats.open_incidents} open`, color: 'text-rose-400' },
                      { label: 'Service Req', value: stats.service_requests, color: 'text-blue-400' },
                      { label: 'Change Req', value: stats.change_requests, color: 'text-purple-400' },
                      { label: 'Total Tickets', value: stats.total_tickets, color: 'text-white' },
                      { label: 'Max Users', value: stats.max_users, color: 'text-amber-400' },
                    ].map(s => (
                      <div key={s.label} className="bg-slate-800/60 rounded-xl p-2.5">
                        <p className="text-xs text-slate-500">{s.label}</p>
                        <p className={`text-base font-bold ${s.color}`}>{s.value}</p>
                        {s.sub && <p className="text-xs text-slate-600">{s.sub}</p>}
                      </div>
                    ))}
                  </div>
                  {statsError && <p className="text-xs text-amber-400 mt-1">Partial data — {statsError}</p>}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Loading statistics...</p>
              )}
            </div>

            {/* Right: Actions */}
            <div>
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Actions</h4>
              <div className="grid grid-cols-2 gap-2">
                {/* Edit */}
                <button onClick={() => setEditMode(true)}
                  className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white px-3 py-2.5 rounded-xl text-xs transition-all border border-slate-700 hover:border-slate-600">
                  <Edit className="w-3.5 h-3.5 text-sky-400" /> Edit Plan / Users
                </button>

                {/* Suspend / Reactivate */}
                {t.is_active ? (
                  <button onClick={handleSuspend} disabled={isLoading('suspend')}
                    className="flex items-center gap-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 px-3 py-2.5 rounded-xl text-xs transition-all border border-amber-500/20 disabled:opacity-50">
                    {isLoading('suspend') ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ToggleLeft className="w-3.5 h-3.5" />} Suspend Tenant
                  </button>
                ) : (
                  <button onClick={handleReactivate} disabled={isLoading('reactivate')}
                    className="flex items-center gap-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 px-3 py-2.5 rounded-xl text-xs transition-all border border-emerald-500/20 disabled:opacity-50">
                    {isLoading('reactivate') ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ToggleRight className="w-3.5 h-3.5" />} Reactivate
                  </button>
                )}

                {/* Reset admin password */}
                <button onClick={() => setShowResetPw(true)}
                  className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white px-3 py-2.5 rounded-xl text-xs transition-all border border-slate-700">
                  <KeyRound className="w-3.5 h-3.5 text-amber-400" /> Reset Admin Password
                </button>

                {/* Impersonate */}
                <button onClick={handleImpersonate} disabled={isLoading('impersonate') || !t.is_active}
                  className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white px-3 py-2.5 rounded-xl text-xs transition-all border border-slate-700 disabled:opacity-50">
                  {isLoading('impersonate') ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5 text-sky-400" />} Login as Admin
                </button>

                {/* Backup */}
                <button onClick={handleBackup} disabled={isLoading('backup')}
                  className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white px-3 py-2.5 rounded-xl text-xs transition-all border border-slate-700 disabled:opacity-50">
                  {isLoading('backup') ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5 text-emerald-400" />}
                  {isLoading('backup') ? 'Backing up...' : 'Backup Database'}
                </button>

                {/* Setup SSL */}
                <button onClick={handleSetupNginx} disabled={isLoading('nginx')}
                  title={`Re-run Nginx config + SSL certbot for ${t.domain}`}
                  className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white px-3 py-2.5 rounded-xl text-xs transition-all border border-slate-700 disabled:opacity-50">
                  {isLoading('nginx') ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5 text-sky-400" />}
                  {isLoading('nginx') ? 'Setting up SSL...' : 'Setup Nginx / SSL'}
                </button>

                {/* Delete */}
                <button onClick={() => setShowDelete(true)}
                  className="flex items-center gap-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 px-3 py-2.5 rounded-xl text-xs transition-all border border-rose-500/20">
                  <Trash2 className="w-3.5 h-3.5" /> Delete Tenant
                </button>

                {/* Retry provision — only shown when pending */}
                {!t.is_provisioned && (
                  <button onClick={handleReprovision} disabled={isLoading('reprovision')}
                    className="col-span-2 flex items-center justify-center gap-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 px-3 py-2.5 rounded-xl text-xs transition-all border border-amber-500/20 disabled:opacity-50">
                    {isLoading('reprovision') ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    Retry Provisioning (DB is Pending)
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Edit form */}
          {editMode && (
            <div className="mt-5 bg-slate-800/60 rounded-xl p-4 border border-slate-700/50">
              <h5 className="text-sm font-semibold text-white mb-3">Edit Tenant</h5>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Name</label>
                  <input value={editForm.name} onChange={e => setEditForm(p => ({...p, name: e.target.value}))}
                    className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Plan</label>
                  <select value={editForm.plan} onChange={e => setEditForm(p => ({...p, plan: e.target.value}))}
                    className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm">
                    <option value="free">Free</option>
                    <option value="standard">Standard ($49/mo)</option>
                    <option value="enterprise">Enterprise ($199/mo)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Max Users</label>
                  <input type="number" value={editForm.max_users} onChange={e => setEditForm(p => ({...p, max_users: parseInt(e.target.value)}))}
                    className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={handleSave} disabled={isLoading('edit')}
                  className="bg-sky-500 hover:bg-sky-400 text-white px-4 py-2 rounded-lg text-xs font-semibold disabled:opacity-50 flex items-center gap-1.5">
                  {isLoading('edit') ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />} Save Changes
                </button>
                <button onClick={() => setEditMode(false)} className="bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-xs">Cancel</button>
              </div>
            </div>
          )}

          {/* Reset password form */}
          {showResetPw && (
            <div className="mt-3 bg-amber-900/20 rounded-xl p-4 border border-amber-500/20">
              <h5 className="text-sm font-semibold text-amber-400 mb-2">Reset Admin Password</h5>
              <div className="flex gap-2">
                <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
                  placeholder="New password (min 8 chars)"
                  className="flex-1 bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm" />
                <button onClick={handleResetPassword} disabled={isLoading('reset')}
                  className="bg-amber-500 text-white px-4 py-2 rounded-lg text-xs font-semibold disabled:opacity-50 flex items-center gap-1.5">
                  {isLoading('reset') ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />} Reset
                </button>
                <button onClick={() => setShowResetPw(false)} className="bg-slate-700 text-slate-300 px-3 py-2 rounded-lg text-xs">Cancel</button>
              </div>
            </div>
          )}

          {/* Backup download link */}
          {lastBackup && (
            <div className="mt-3 bg-emerald-900/20 rounded-xl p-3 border border-emerald-500/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Download className="w-4 h-4 text-emerald-400" />
                <div>
                  <p className="text-xs text-emerald-400 font-semibold">Backup ready — {lastBackup.file_size_mb}MB</p>
                  <p className="text-xs text-slate-500">{lastBackup.filename}</p>
                </div>
              </div>
              <a
                href={lastBackup.download_url}
                download={lastBackup.filename}
                onClick={() => {
                  // Add auth header via fetch for download
                  const token = localStorage.getItem('superadmin_token');
                  fetch(lastBackup.download_url, { headers: { Authorization: `Bearer ${token}` } })
                    .then(r => r.blob())
                    .then(b => {
                      const url = URL.createObjectURL(b);
                      const a = document.createElement('a');
                      a.href = url; a.download = lastBackup.filename; a.click();
                      URL.revokeObjectURL(url);
                    })
                    .catch(() => toast.error('Download failed'));
                  return false;
                }}
                className="bg-emerald-500 hover:bg-emerald-400 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" /> Download
              </a>
            </div>
          )}
          {showDelete && (
            <div className="mt-3 bg-rose-900/20 rounded-xl p-4 border border-rose-500/20">
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-rose-400 font-semibold text-sm">Permanently delete "{t.name}"?</p>
                  <p className="text-slate-400 text-xs mt-1">This will DROP the database <code className="text-rose-300 bg-slate-800 px-1 rounded">{t.db_name}</code> and remove all data. This cannot be undone.</p>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <input value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)}
                  placeholder={`Type "${t.slug}" to confirm`}
                  className="flex-1 bg-slate-900 border border-rose-500/30 text-white rounded-lg px-3 py-2 text-sm font-mono" />
                <button onClick={handleDelete} disabled={isLoading('delete') || deleteConfirm !== t.slug}
                  className="bg-rose-500 text-white px-4 py-2 rounded-lg text-xs font-semibold disabled:opacity-50 flex items-center gap-1.5">
                  {isLoading('delete') ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete
                </button>
                <button onClick={() => { setShowDelete(false); setDeleteConfirm(''); }} className="bg-slate-700 text-slate-300 px-3 py-2 rounded-lg text-xs">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ── Create tenant modal ───────────────────────────────────────────────────────
function CreateTenantModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', slug: '', domain: '', admin_email: '', plan: 'standard', max_users: 50 });
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [dns, setDns] = useState(null); // {resolved, ip, server_ip, matches, error, checking}

  const effectiveDomain = form.domain.trim() || (form.slug ? `${form.slug}.supportone.trivont.com` : '');

  const handleCheckDns = async () => {
    if (!effectiveDomain) { toast.error('Enter domain or slug first'); return; }
    setDns({ checking: true });
    try {
      const res = await saAxios.get(`/check-dns/?domain=${encodeURIComponent(effectiveDomain)}`);
      setDns({ ...res.data, checking: false });
    } catch (err) {
      setDns({ checking: false, resolved: false, error: err.response?.data?.error || 'DNS check failed' });
    }
  };

  // Reset DNS check when domain changes
  const handleDomainChange = (val) => {
    setForm(p => ({...p, domain: val}));
    setDns(null);
  };
  const handleSlugChange = (val) => {
    setForm(p => ({...p, slug: val.toLowerCase().replace(/[^a-z0-9_]/g, '_')}));
    setDns(null);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug) { toast.error('Name and slug required'); return; }
    if (!form.admin_email) { toast.error('Admin email is required'); return; }
    if (!dns || !dns.resolved || !dns.matches) {
      toast.error('Please verify DNS first — click "Verify DNS"');
      return;
    }
    setCreating(true);
    try {
      const payload = { ...form, domain: effectiveDomain };
      const res = await saAxios.post('/tenants/', payload);
      setResult(res.data);
      toast.success('Tenant provisioned with SSL!');
      onCreated();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || 'Failed';
      toast.error(msg);
    } finally { setCreating(false); }
  };

  const dnsOk = dns && dns.resolved && dns.matches;
  const dnsChecked = dns && !dns.checking;

  if (result) {
    const portalUrl = result.portal_url || `https://${result.tenant?.domain}`;
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-slate-900 rounded-2xl border border-emerald-500/30 p-6 w-full max-w-md shadow-2xl">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-7 h-7 text-emerald-400" />
            </div>
            <h3 className="text-white font-bold text-lg">Tenant Created!</h3>
            <p className="text-slate-400 text-sm mt-1">{result.tenant?.name} is live with SSL</p>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 space-y-3 text-sm mb-4">
            <div className="flex justify-between">
              <span className="text-slate-400">Portal URL</span>
              <a href={portalUrl} target="_blank" rel="noopener noreferrer"
                className="text-sky-400 hover:text-sky-300 font-mono flex items-center gap-1">
                {result.tenant?.domain} <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Admin Email</span>
              <span className="text-white font-mono">{result.admin_email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Password</span>
              <span className="text-amber-400 font-mono">{result.admin_password}</span>
            </div>
          </div>
          <p className="text-xs text-slate-500 text-center mb-4">Share these credentials with the tenant admin.</p>
          <button onClick={onClose} className="w-full bg-sky-500 hover:bg-sky-400 text-white py-2.5 rounded-xl text-sm font-semibold">Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-2xl border border-slate-700/50 p-6 w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-white font-bold text-lg flex items-center gap-2"><Building2 className="w-5 h-5 text-sky-400" /> New Tenant</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-white p-1 rounded-lg hover:bg-slate-800"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 font-medium mb-1.5 block">Tenant Name *</label>
            <input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} required
              placeholder="e.g. ACME Corporation"
              className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-500" />
          </div>
          <div>
            <label className="text-xs text-slate-400 font-medium mb-1.5 block">Slug * (URL identifier)</label>
            <input value={form.slug} onChange={e => handleSlugChange(e.target.value)} required
              placeholder="e.g. acme"
              className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-sky-500" />
            {form.slug && <p className="text-xs text-slate-500 mt-1">DB: <code className="text-emerald-400">itsm_{form.slug}</code></p>}
          </div>
          <div>
            <label className="text-xs text-slate-400 font-medium mb-1.5 block">Admin Email * <span className="text-slate-500">(login credential)</span></label>
            <input type="email" value={form.admin_email} onChange={e => setForm(p => ({...p, admin_email: e.target.value}))} required
              placeholder="e.g. admin@acme.com"
              className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-500" />
            <p className="text-xs text-slate-500 mt-1">Default password: <code className="text-amber-400">abc123def</code></p>
          </div>

          {/* Domain + DNS Verify */}
          <div>
            <label className="text-xs text-slate-400 font-medium mb-1.5 block">
              Domain <span className="text-slate-500">(leave blank to auto-generate)</span>
            </label>
            <div className="flex gap-2">
              <input value={form.domain} onChange={e => handleDomainChange(e.target.value)}
                placeholder={form.slug ? `${form.slug}.supportone.trivont.com` : 'subdomain.supportone.trivont.com'}
                className="flex-1 bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-500" />
              <button type="button" onClick={handleCheckDns}
                disabled={!effectiveDomain || dns?.checking}
                className="px-3 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-xl text-xs font-medium disabled:opacity-40 whitespace-nowrap flex items-center gap-1.5">
                {dns?.checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {dns?.checking ? 'Checking...' : 'Verify DNS'}
              </button>
            </div>

            {/* DNS status */}
            {dnsChecked && (
              <div className={`mt-2 p-2.5 rounded-lg text-xs flex items-start gap-2 ${
                dnsOk ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                       : 'bg-rose-500/10 border border-rose-500/20 text-rose-400'
              }`}>
                {dnsOk
                  ? <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  : <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
                <div>
                  {dnsOk
                    ? <>DNS verified — <code className="font-mono">{dns.ip}</code> matches this server. SSL will be provisioned automatically.</>
                    : <>{dns.error || 'DNS check failed.'}<br/>
                        <span className="text-rose-300">
                          Add DNS A record: <code className="font-mono">{effectiveDomain}</code> → <code className="font-mono">{dns.server_ip && dns.server_ip !== 'unknown' ? dns.server_ip : '<server IP>'}</code>
                        </span>
                      </>
                  }
                </div>
              </div>
            )}
            {!dnsChecked && effectiveDomain && (
              <p className="text-xs text-amber-400/70 mt-1.5">
                Click "Verify DNS" — must pass before tenant can be created with SSL
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 font-medium mb-1.5 block">Plan</label>
              <select value={form.plan} onChange={e => setForm(p => ({...p, plan: e.target.value}))}
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-sky-500">
                <option value="free">Free</option>
                <option value="standard">Standard ($49/mo)</option>
                <option value="enterprise">Enterprise ($199/mo)</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 font-medium mb-1.5 block">Max Users</label>
              <input type="number" value={form.max_users} onChange={e => setForm(p => ({...p, max_users: parseInt(e.target.value) || 50}))}
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-500" />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 py-2.5 rounded-xl text-sm">Cancel</button>
            <button type="submit" disabled={creating || !dnsOk}
              title={!dnsOk ? 'Verify DNS first' : ''}
              className="flex-1 bg-sky-500 hover:bg-sky-400 disabled:bg-slate-700 disabled:text-slate-500 text-white py-2.5 rounded-xl text-sm font-semibold disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {creating ? 'Provisioning...' : 'Create & Provision'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────
export default function SuperAdminPage() {
  const [saUser, setSaUser] = useState(() => {
    const token = localStorage.getItem(SA_TOKEN_KEY);
    if (!token) return null;
    try {
      const p = JSON.parse(atob(token.split('.')[1]));
      if (p.exp * 1000 < Date.now()) { localStorage.removeItem(SA_TOKEN_KEY); return null; }
      return p;
    } catch { return null; }
  });

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [activeSection, setActiveSection] = useState('tenants');
  // Section-specific data
  const [billing, setBilling] = useState({ bills: [], summary: {} });
  const [auditLogs, setAuditLogs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [backups, setBackups] = useState([]);
  // Form states
  const [broadcastForm, setBroadcastForm] = useState({ subject: '', message: '' });
  const [annForm, setAnnForm] = useState({ message: '', type: 'info' });
  const [actionLoading, setActionLoading] = useState('');

  const handleLogin = (data) => {
    setSaUser(data);
    if (data.tenants) setTenants(data.tenants);
  };

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const res = await saAxios.get('/tenants/');
      setTenants(res.data?.results || res.data || []);
    } catch { toast.error('Could not refresh list'); }
    finally { setLoading(false); }
  }, []);

  const fetchSection = useCallback(async (section) => {
    try {
      if (section === 'billing') {
        const res = await saAxios.get('/billing/');
        setBilling(res.data);
      } else if (section === 'audit') {
        const res = await saAxios.get('/audit/');
        setAuditLogs(res.data);
      } else if (section === 'announcements') {
        const res = await saAxios.get('/announcements/');
        setAnnouncements(res.data);
      } else if (section === 'backups') {
        const res = await saAxios.get('/backups/');
        setBackups(res.data);
      }
    } catch (err) {
      if (err.response?.status !== 500) toast.error('Failed to load data');
    }
  }, []);

  // On mount: if already logged in from localStorage, fetch tenants immediately
  // (fixes empty table on browser refresh)
  useEffect(() => {
    if (saUser) fetchTenants();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (saUser?.tenants) setTenants(saUser.tenants);
  }, [saUser]);

  useEffect(() => {
    if (saUser && activeSection !== 'tenants') {
      fetchSection(activeSection);
    }
  }, [activeSection, saUser, fetchSection]);

  const handleLogout = () => {
    localStorage.removeItem(SA_TOKEN_KEY);
    setSaUser(null);
    setTenants([]);
    toast.success('Logged out');
  };

  const handleBroadcast = async () => {
    if (!broadcastForm.subject || !broadcastForm.message) { toast.error('Subject and message required'); return; }
    setActionLoading('broadcast');
    try {
      const res = await saAxios.post('/broadcast/', broadcastForm);
      toast.success(`Email sent to ${res.data.sent} tenants`);
      setBroadcastForm({ subject: '', message: '' });
    } catch { toast.error('Broadcast failed'); }
    finally { setActionLoading(''); }
  };

  const handleGenerateInvoices = async () => {
    setActionLoading('invoices');
    try {
      const res = await saAxios.post('/billing/generate/');
      toast.success(res.data.detail);
      fetchSection('billing');
    } catch { toast.error('Failed'); }
    finally { setActionLoading(''); }
  };

  const handleMarkPaid = async (billId) => {
    try {
      await saAxios.patch(`/billing/${billId}/`, { status: 'paid', paid_date: new Date().toISOString().split('T')[0] });
      toast.success('Marked as paid');
      fetchSection('billing');
    } catch { toast.error('Failed'); }
  };

  const handleAddAnnouncement = async () => {
    if (!annForm.message) { toast.error('Message required'); return; }
    setActionLoading('ann');
    try {
      await saAxios.post('/announcements/', annForm);
      toast.success('Announcement created');
      setAnnForm({ message: '', type: 'info' });
      fetchSection('announcements');
    } catch { toast.error('Failed'); }
    finally { setActionLoading(''); }
  };

  const handleToggleAnnouncement = async (ann) => {
    try {
      await saAxios.patch(`/announcements/${ann.id}/`, { is_active: !ann.is_active });
      fetchSection('announcements');
    } catch { toast.error('Failed'); }
  };

  const handleDeleteAnnouncement = async (id) => {
    try {
      await saAxios.delete(`/announcements/${id}/`);
      toast.success('Deleted');
      fetchSection('announcements');
    } catch { toast.error('Failed'); }
  };

  if (!saUser) return <LoginScreen onLogin={handleLogin} />;

  const filtered = tenants.filter(t =>
    t.name?.toLowerCase().includes(search.toLowerCase()) ||
    t.slug?.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: tenants.length,
    active: tenants.filter(t => t.is_active).length,
    provisioned: tenants.filter(t => t.is_provisioned).length,
    enterprise: tenants.filter(t => t.plan === 'enterprise').length,
    mrr: tenants.filter(t => t.is_active).reduce((s, t) => s + (PLAN_PRICE[t.plan] || 0), 0),
  };

  const navItems = [
    { id: 'tenants',       label: 'Tenants',         icon: Building2,    badge: tenants.length },
    { id: 'billing',       label: 'Billing',          icon: DollarSign },
    { id: 'announcements', label: 'Announcements',    icon: Bell,          badge: announcements.filter(a => a.is_active).length || null },
    { id: 'broadcast',     label: 'Email Broadcast',  icon: Send },
    { id: 'backups',       label: 'DB Backups',       icon: Download },
    { id: 'audit',         label: 'Audit Trail',      icon: ClipboardList },
  ];

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="superadmin-page">
      {showCreate && <CreateTenantModal onClose={() => setShowCreate(false)} onCreated={fetchTenants} />}

      {/* Sidebar */}
      <div className="w-60 bg-slate-900/90 border-r border-slate-800/60 flex flex-col flex-shrink-0">
        <div className="px-5 py-5 border-b border-slate-800/60">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-sky-400 to-sky-600 rounded-xl flex items-center justify-center shadow-lg">
              <Shield style={{width:18,height:18}} className="text-white" />
            </div>
            <div>
              <p className="font-black text-white text-sm">Superadmin</p>
              <p className="text-xs text-slate-500">Tenant Console</p>
            </div>
          </div>
        </div>

        {/* Stats summary in sidebar */}
        <div className="px-4 py-3 border-b border-slate-800/40">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-slate-800/50 rounded-xl p-2.5 text-center">
              <p className="text-lg font-black text-emerald-400">{stats.active}</p>
              <p className="text-xs text-slate-500">Active</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-2.5 text-center">
              <p className="text-lg font-black text-sky-400">${stats.mrr}</p>
              <p className="text-xs text-slate-500">MRR</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
          {navItems.map(item => (
            <button key={item.id} onClick={() => setActiveSection(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                activeSection === item.id
                  ? 'bg-sky-500/15 text-sky-400 border border-sky-500/20'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}>
              <item.icon className="w-4 h-4 flex-shrink-0" />
              {item.label}
              {item.badge != null && item.badge > 0 && (
                <span className="ml-auto bg-slate-700 text-slate-300 text-xs px-2 py-0.5 rounded-full">{item.badge}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-slate-800/60">
          <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-slate-800/50 mb-2">
            <div className="w-7 h-7 bg-sky-500/20 rounded-lg flex items-center justify-center">
              <Shield style={{width:14,height:14}} className="text-sky-400" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-white truncate">{saUser.sa_name || 'Super Admin'}</p>
              <p className="text-xs text-slate-500 truncate">{saUser.sa_email || ''}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 text-sm transition-all">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-auto">
        {/* Header */}
        <div className="border-b border-slate-800/60 bg-slate-900/40 backdrop-blur-sm px-8 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h1 className="text-lg font-bold text-white">{navItems.find(n => n.id === activeSection)?.label || 'Dashboard'}</h1>
            <p className="text-xs text-slate-500 mt-0.5">{stats.total} tenants · ${stats.mrr}/mo MRR · {stats.active} active</p>
          </div>
          {activeSection === 'tenants' && (
            <button onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-white px-4 py-2 rounded-xl text-sm font-semibold shadow-lg shadow-sky-500/20">
              <Plus className="w-4 h-4" /> New Tenant
            </button>
          )}
        </div>

        <div className="flex-1 p-8">
          {/* Stats row */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <StatCard label="Total Tenants" value={stats.total} icon={Building2} />
            <StatCard label="Active" value={stats.active} color="text-emerald-400" icon={CheckCircle} />
            <StatCard label="Provisioned" value={stats.provisioned} color="text-sky-400" icon={Database} />
            <StatCard label="Enterprise" value={stats.enterprise} color="text-purple-400" icon={Crown} />
            <StatCard label="Monthly Revenue" value={`$${stats.mrr}`} color="text-amber-400" icon={DollarSign} sub="active tenants" />
          </div>

          {/* ── TENANTS ── */}
          {activeSection === 'tenants' && (
            <>
              <div className="flex items-center gap-3 mb-5">
                <div className="relative flex-1">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input type="text" placeholder="Search tenants..." value={search} onChange={e => setSearch(e.target.value)}
                    className="w-full bg-slate-900/80 border border-slate-700/50 text-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-sky-500 transition-all" />
                </div>
                <button onClick={fetchTenants} disabled={loading}
                  className="p-2.5 bg-slate-800 hover:bg-slate-700 rounded-xl border border-slate-700/50 text-slate-400 hover:text-white transition-all">
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>

              <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl overflow-hidden">
                <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-slate-800/60 bg-slate-900/80">
                  {[['Tenant', 3], ['Database', 2], ['Plan', 1], ['Status', 1], ['Provisioned', 1], ['Created', 1], ['', 3]].map(([h, span], i) => (
                    <div key={i} className={`col-span-${span} text-xs font-semibold text-slate-400 uppercase tracking-wider`}>{h}</div>
                  ))}
                </div>
                {loading ? (
                  <div className="flex justify-center py-14"><Loader2 className="w-7 h-7 animate-spin text-sky-500" /></div>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-14">
                    <Building2 className="w-10 h-10 mx-auto mb-3 text-slate-700" />
                    <p className="text-slate-500 text-sm">{search ? `No tenants matching "${search}"` : 'No tenants yet'}</p>
                    {!search && <button onClick={() => setShowCreate(true)} className="mt-3 text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1 mx-auto"><Plus className="w-4 h-4" /> Create your first tenant</button>}
                  </div>
                ) : filtered.map(t => <TenantRow key={t.id} t={t} onRefresh={fetchTenants} />)}
              </div>
            </>
          )}

          {/* ── BILLING ── */}
          {activeSection === 'billing' && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <StatCard label="Total Revenue" value={`$${billing.summary?.total_revenue?.toFixed(0) || 0}`} color="text-emerald-400" icon={DollarSign} />
                <StatCard label="Overdue" value={billing.summary?.overdue_count || 0} color="text-rose-400" icon={AlertTriangle} />
                <StatCard label="Pending" value={billing.summary?.pending_count || 0} color="text-amber-400" icon={FileText} />
              </div>
              {/* Actions */}
              <div className="flex gap-3">
                <button onClick={handleGenerateInvoices} disabled={actionLoading === 'invoices'}
                  className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-4 py-2 rounded-xl text-sm font-semibold disabled:opacity-50">
                  {actionLoading === 'invoices' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                  Generate This Month's Invoices
                </button>
              </div>
              {/* Bills table */}
              <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900/80 border-b border-slate-800">
                    <tr>{['Tenant','Plan','Amount','Period','Due Date','Status',''].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {billing.bills?.length === 0 ? (
                      <tr><td colSpan={7} className="text-center text-slate-500 py-10 text-sm">No billing records yet. Click "Generate Invoices" to create.</td></tr>
                    ) : billing.bills?.map(b => (
                      <tr key={b.id} className="hover:bg-slate-800/30">
                        <td className="px-4 py-3 font-medium text-white text-xs">{b.tenant_name}</td>
                        <td className="px-4 py-3"><PlanBadge plan={b.plan} /></td>
                        <td className="px-4 py-3 text-emerald-400 font-semibold text-xs">${b.amount}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{b.billing_period}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{b.due_date}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
                            b.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                            b.status === 'overdue' ? 'bg-rose-500/20 text-rose-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>{b.status}</span>
                        </td>
                        <td className="px-4 py-3">
                          {b.status === 'pending' && (
                            <button onClick={() => handleMarkPaid(b.id)}
                              className="text-xs text-emerald-400 hover:text-emerald-300 px-2.5 py-1 rounded-lg hover:bg-emerald-500/10 transition-all">
                              Mark Paid
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── ANNOUNCEMENTS ── */}
          {activeSection === 'announcements' && (
            <div className="space-y-5">
              {/* Add new */}
              <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Bell className="w-4 h-4 text-amber-400" /> New Announcement</h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-4 gap-3">
                    <div className="col-span-3">
                      <textarea value={annForm.message} onChange={e => setAnnForm(p => ({...p, message: e.target.value}))}
                        placeholder="Announcement message (shown as banner across all tenant portals)..."
                        rows={2}
                        className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:border-sky-500" />
                    </div>
                    <div>
                      <select value={annForm.type} onChange={e => setAnnForm(p => ({...p, type: e.target.value}))}
                        className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-sky-500 mb-3">
                        <option value="info">Info</option>
                        <option value="warning">Warning</option>
                        <option value="critical">Critical</option>
                        <option value="maintenance">Maintenance</option>
                      </select>
                      <button onClick={handleAddAnnouncement} disabled={actionLoading === 'ann'}
                        className="w-full bg-amber-500 hover:bg-amber-400 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                        {actionLoading === 'ann' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Post
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* List */}
              <div className="space-y-3">
                {announcements.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-8">No announcements. Create one above to broadcast to all tenants.</p>
                ) : announcements.map(a => (
                  <div key={a.id} className={`bg-slate-900/60 border rounded-xl p-4 flex items-start justify-between gap-4 ${
                    a.is_active ? 'border-amber-500/30' : 'border-slate-700/50 opacity-60'
                  }`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                          a.type === 'critical' ? 'bg-rose-500/20 text-rose-400' :
                          a.type === 'warning' ? 'bg-amber-500/20 text-amber-400' :
                          a.type === 'maintenance' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-sky-500/20 text-sky-400'
                        }`}>{a.type}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${a.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-500'}`}>
                          {a.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-white text-sm">{a.message}</p>
                      <p className="text-xs text-slate-500 mt-1">By {a.created_by} · {new Date(a.created_at).toLocaleString()}</p>
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={() => handleToggleAnnouncement(a)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white">
                        {a.is_active ? <ToggleRight className="w-4 h-4 text-emerald-400" /> : <ToggleLeft className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleDeleteAnnouncement(a.id)} className="p-1.5 rounded-lg hover:bg-rose-500/10 text-slate-500 hover:text-rose-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── EMAIL BROADCAST ── */}
          {activeSection === 'broadcast' && (
            <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl p-6 max-w-2xl">
              <h3 className="text-white font-semibold mb-5 flex items-center gap-2"><Send className="w-4 h-4 text-sky-400" /> Email All Tenant Admins</h3>
              <p className="text-sm text-slate-400 mb-5">This will send an email to the primary admin of each active tenant. Use for maintenance notices, feature announcements, or important updates.</p>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-slate-400 font-medium block mb-2">Subject *</label>
                  <input value={broadcastForm.subject} onChange={e => setBroadcastForm(p => ({...p, subject: e.target.value}))}
                    placeholder="e.g. Scheduled Maintenance - Sunday 2 AM UTC"
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-sky-500" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 font-medium block mb-2">Message *</label>
                  <textarea value={broadcastForm.message} onChange={e => setBroadcastForm(p => ({...p, message: e.target.value}))}
                    placeholder="Write your message here..." rows={6}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:border-sky-500" />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-500">Will be sent to <strong className="text-slate-300">{stats.active}</strong> active tenant admin{stats.active !== 1 ? 's' : ''}</p>
                  <button onClick={handleBroadcast} disabled={actionLoading === 'broadcast'}
                    className="flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-white px-6 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 shadow-lg shadow-sky-500/20">
                    {actionLoading === 'broadcast' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Send to All Tenants
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── DB BACKUPS ── */}
          {activeSection === 'backups' && (
            <div className="space-y-5">
              <div className="bg-sky-900/20 border border-sky-500/20 rounded-xl p-4 flex gap-3">
                <Info className="w-5 h-5 text-sky-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-sky-300">Go to the <strong>Tenants</strong> tab, expand any tenant, and click <strong>"Backup Database"</strong> to trigger a pg_dump backup for that specific tenant. Completed backups appear here.</p>
              </div>
              <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900/80 border-b border-slate-800">
                    <tr>{['Tenant','Backup File','Size','Status','Triggered By','Date','Download'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {backups.length === 0 ? (
                      <tr><td colSpan={7} className="text-center text-slate-500 py-10 text-sm">No backups yet</td></tr>
                    ) : backups.map(b => (
                      <tr key={b.id} className="hover:bg-slate-800/30">
                        <td className="px-4 py-3 font-medium text-white text-xs">{b.tenant_name}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-400 max-w-xs truncate">{b.backup_file.split('/').pop()}</td>
                        <td className="px-4 py-3 text-xs text-slate-400">{b.file_size_mb ? `${b.file_size_mb}MB` : '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
                            b.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                            b.status === 'failed' ? 'bg-rose-500/20 text-rose-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>{b.status}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">{b.triggered_by}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{new Date(b.created_at).toLocaleString()}</td>
                        <td className="px-4 py-3">
                          {b.status === 'completed' && (
                            <button
                              onClick={() => {
                                const token = localStorage.getItem('superadmin_token');
                                fetch(`/api/superadmin/backups/${b.id}/download/`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                })
                                  .then(r => r.blob())
                                  .then(blob => {
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = b.backup_file.split('/').pop();
                                    a.click();
                                    URL.revokeObjectURL(url);
                                  })
                                  .catch(() => toast.error('Download failed'));
                              }}
                              className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 px-2.5 py-1.5 rounded-lg hover:bg-emerald-500/10 transition-all"
                            >
                              <Download className="w-3.5 h-3.5" /> Download
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── AUDIT TRAIL ── */}
          {activeSection === 'audit' && (
            <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/80 border-b border-slate-800">
                  <tr>{['Action','Tenant','Performed By','Details','When'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {auditLogs.length === 0 ? (
                    <tr><td colSpan={5} className="text-center text-slate-500 py-10 text-sm">No audit log entries yet</td></tr>
                  ) : auditLogs.map(l => (
                    <tr key={l.id} className="hover:bg-slate-800/30">
                      <td className="px-4 py-3">
                        <span className="text-xs bg-slate-700 text-slate-300 px-2.5 py-1 rounded-lg font-mono">{l.action}</span>
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-sky-400">{l.tenant_slug || '—'}</td>
                      <td className="px-4 py-3 text-xs text-slate-300">{l.performed_by}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 max-w-xs truncate">{l.details || '—'}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
